import { useEffect, useMemo, useState } from 'react';

const PAGE_SIZE = 10;

function computeStats(productReviews) {
  const total = productReviews.length;
  const sum = productReviews.reduce((acc, r) => acc + (r.rating || 0), 0);
  const average = total ? sum / total : 0;

  const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
  productReviews.forEach((r) => {
    if (distribution[r.rating] !== undefined) distribution[r.rating] += 1;
  });

  const activeCount = productReviews.filter((r) => !r.isDeleted).length;
  const activePercentage = total ? Math.round((activeCount / total) * 100) : 0;

  return { total, average, distribution, activeCount, activePercentage };
}

// Filtra as avaliações do produto selecionado (tabs Todas/Ativas/Inativas +
// busca por nome/mensagem + filtros de nota mínima e período) e pagina o
// resultado. Separado de useReviews pra manter a busca de dados isolada da
// lógica de exibição.
export function useReviewFilters(reviews, selectedProductId) {
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [ratingMin, setRatingMin] = useState(0);
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [selectedProductId, tab, search, ratingMin, dateStart, dateEnd]);

  const productReviews = useMemo(
    () => reviews.filter((r) => !selectedProductId || r.product?._id === selectedProductId),
    [reviews, selectedProductId]
  );

  const stats = useMemo(() => computeStats(productReviews), [productReviews]);

  const counts = useMemo(
    () => ({
      all: productReviews.length,
      active: productReviews.filter((r) => !r.isDeleted).length,
      inactive: productReviews.filter((r) => r.isDeleted).length,
    }),
    [productReviews]
  );

  const filteredReviews = useMemo(() => {
    const term = search.trim().toLowerCase();
    return productReviews.filter((r) => {
      if (tab === 'active' && r.isDeleted) return false;
      if (tab === 'inactive' && !r.isDeleted) return false;
      if (ratingMin && r.rating < ratingMin) return false;
      if (dateStart && new Date(r.createdAt) < new Date(dateStart)) return false;
      if (dateEnd && new Date(r.createdAt) > new Date(`${dateEnd}T23:59:59`)) return false;
      if (term) {
        const matchesName = r.name?.toLowerCase().includes(term);
        const matchesMessage = r.message?.toLowerCase().includes(term);
        if (!matchesName && !matchesMessage) return false;
      }
      return true;
    });
  }, [productReviews, tab, ratingMin, dateStart, dateEnd, search]);

  const totalPages = Math.max(1, Math.ceil(filteredReviews.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pagedReviews = useMemo(
    () => filteredReviews.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [filteredReviews, safePage]
  );

  return {
    tab,
    setTab,
    search,
    setSearch,
    ratingMin,
    setRatingMin,
    dateStart,
    setDateStart,
    dateEnd,
    setDateEnd,
    page: safePage,
    setPage,
    totalPages,
    pageSize: PAGE_SIZE,
    productReviews,
    filteredReviews,
    pagedReviews,
    stats,
    counts,
  };
}
